import { Injectable } from '@angular/core';
import { DateTimeService } from '@examdojo/core/date-time';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { UserQuery } from '@examdojo/user';
import kebabCase from 'lodash/kebabCase';
import { finalize, switchMap, tap, throwError } from 'rxjs';
import { ClassHttpService } from './class-http.service';
import { ClassFormData, ClassHttpModel, ClassStoreModel } from './class.model';
import { ClassQuery } from './class.query';
import { ClassStore } from './class.store';

@Injectable({ providedIn: 'root' })
export class ClassService {
  constructor(
    private readonly store: ClassStore,
    private readonly http: ClassHttpService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly query: ClassQuery,
    private readonly userQuery: UserQuery,
    private readonly dateTimeService: DateTimeService,
  ) {}

  fetchClasses() {
    this.store.setLoading(true);

    return this.http.fetchAllClassesView().pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class', action: 'fetch' }),
      tap((classes) => this.store.set(classes.map((c) => this.mapToStoreModel(c)))),
      finalize(() => this.store.setLoading(false)),
    );
  }

  setActiveClass(id: string | null) {
    this.store.setActive(id);
  }

  setActiveBySlug(slug: string): boolean {
    const classEntity = this.query.getAllUIEntities().find((c) => c.slug === slug);
    if (!classEntity) {
      return false;
    }
    this.store.setActive(classEntity.id);
    return true;
  }

  createClass(data: ClassFormData) {
    const currentUser = this.userQuery.getActive();
    if (!currentUser) {
      return throwError(() => new Error('User not found'));
    }

    return this.http.create({ ...data, teacher_id: currentUser.id }).pipe(
      switchMap((newClass) =>
        this.http.fetchClassViewById(newClass.id!).pipe(tap((classFromView) => this.updateEntity(classFromView))),
      ),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class', action: 'create' }),
    );
  }

  updateClass(id: string, data: ClassFormData) {
    const currentUser = this.userQuery.getActive();
    if (!currentUser) {
      return throwError(() => new Error('User not found'));
    }

    return this.http.update(id, data).pipe(
      switchMap((updated) =>
        this.http.fetchClassViewById(updated.id!).pipe(tap((classFromView) => this.updateEntity(classFromView))),
      ),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class', action: 'update' }),
    );
  }

  archiveClass(id: string) {
    return this.http.archive(id).pipe(
      tap(() => this.store.update(id, { archived_at: new Date().toISOString() })),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class', action: 'update' }),
    );
  }

  unarchiveClass(id: string) {
    return this.http.unarchive(id).pipe(
      tap(() => this.store.update(id, { archived_at: null })),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class', action: 'update' }),
    );
  }

  deleteClass(id: string) {
    return this.http.delete(id).pipe(
      tap(() => this.store.delete(id)),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class', action: 'delete' }),
    );
  }

  toggleEnrollmentOpen(id: string) {
    const currentEntity = this.query.getEntity(id);
    const closedAt = currentEntity?.enrollment_closed_at ? null : new Date().toISOString();
    return this.http.setEnrollmentCloseAt(id, closedAt).pipe(
      switchMap(() => this.http.fetchClassViewById(id)),
      tap((classFromView) => this.updateEntity(classFromView)),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class', action: 'update' }),
    );
  }

  resetEnrollmentCode(id: string) {
    return this.http.resetEnrollmentCode(id).pipe(
      tap((newCode) => this.store.update(id, { enrollment_code: newCode })),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class', action: 'update' }),
    );
  }

  private updateEntity(httpModel: ClassHttpModel) {
    const storeModel = this.mapToStoreModel(httpModel);
    this.store.upsert(storeModel.id, storeModel);
  }

  private mapToStoreModel(httpModel: ClassHttpModel): ClassStoreModel {
    const id = httpModel.id ?? '';
    const name = httpModel.name ?? '';
    return {
      ...httpModel,
      id,
      name,
      slug: `${kebabCase(name)}-${id.slice(0, 8)}`,
      exam_first_date: this.dateTimeService.parseDateOnly(httpModel.exam_first_date),
      exam_last_date: this.dateTimeService.parseDateOnly(httpModel.exam_last_date),
    };
  }
}
